import React, { useRef, useEffect } from 'react';

const projects = [
  {
    title: "AI-Powered Safety Wearable Device ",
    description: "An IoT-based wearable device that detects unsafe environments using sensors and alerts users in real-time.",
    tech: ["Python", "TensorFlow", "IoT", "OpenCV"],
    image: "p1.jpg",
    github: "#",
    live: "https://www.youtube.com/watch?v=GjtGuLysAZ8"
  },
  {
    title: "Sentiment Analysis Web App",
    description: "A web app built using React and Node.js that analyzes social media sentiment.",
    tech: ["React", "Node.js", "NLP", "Express"],
    image: "p2.jpg",
    github: "#",
    live: "#"
  },
  {
    title: "Geofencing Attendance System",
    description: "A location-based system that marks attendance only within a predefined GPS boundary.",
    tech: ["React Native", "Firebase", "GeoHash", "Google Maps API"],
    image: "p3.jpg",
    github: "https://github.com/aniketofficial540/Geofencing-Based-Attendace-System",
    live: "#"
  },
  {
    title: "Weapon Detection Model",
    description: "Machine learning model trained on YOLOv8 for real-time weapon detection in video streams.",
    tech: ["YOLOv8", "OpenCV", "Python", "PyTorch"],
    image: "p4.jpg",
    github: "#",
    live: "#"
  },
  {
    title: "EduGo - AI Learning Platform",
    description: "A personalized learning platform that recommends content using AI-driven suggestions.",
    tech: ["React", "TailwindCSS", "Vercel", "Framer Motion"],
    image: "p5.jpg",
    github: "https://github.com/aniketofficial540/Edu-GO---Smart-AI-Based-Learning-Platform",
    live: "#"
  },
  {
    title: "Library Entry Management System",
    description: "An ID-based library entry tracker that records student/faculty check-ins and exports logs via CSV Sheets.",
    tech: ["D3.js", "Flask", "Chart.js", "Pandas"],
    image: "p6.jpg",
    github: "https://github.com/aniketofficial540/Library-Attendance-Management-System",
    live: "#"
  }
];

const Projects = () => {
  const itemsRef = useRef([]);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    itemsRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      itemsRef.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <section id="projects" className="relative py-28 px-6 overflow-hidden bg-transparent">
      
      {/* Background Tech Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 left-[10%] w-32 h-32 border-4 border-blue-500/10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/3 right-[15%] w-24 h-24 border-3 border-cyan-500/10 rounded-full animate-spin-slow opacity-70"></div>
        <div className="absolute top-1/3 right-[25%] w-16 h-16 border-t-4 border-r-4 border-blue-500/10 transform rotate-45 animate-wave"></div>
        <div className="absolute bottom-1/4 left-[25%] w-20 h-20 border-2 border-purple-500/10 rounded-lg transform rotate-12 animate-float-1"></div>
      </div>

      {/* Section Content */}
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            My Projects
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10">
            A collection of my most impactful work in AI, web development, and machine learning.
          </p>
          <div className="w-40 h-1 mx-auto bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {projects.map((project, index) => (
            <div
              key={index}
              ref={(el) => (itemsRef.current[index] = el)}
              className="group relative p-px bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-500 rounded-xl shadow-lg hover:shadow-blue-500/20 transition-all duration-500"
            >
              <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 flex flex-col h-full">
                {/* Image */}
                <img src={project.image} alt={project.title} className="rounded-lg w-full h-40 object-cover mb-4 group-hover:scale-105 transition-transform duration-300" />

                {/* Title */}
                <h3 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-sm mb-4 flex-grow">{project.description}</p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech.map((tech, idx) => (
                    <span key={idx} className="text-xs bg-gray-800 px-2 py-1 rounded-md text-blue-400">
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Links */}
                <div className="flex justify-between mt-auto">
                  <a href={project.github} className="text-blue-400 hover:text-blue-300 transition-colors">GitHub</a>
                  <a href={project.live} className="text-green-400 hover:text-green-300 transition-colors">Live Demo</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes wave {
          0%, 100% { transform: rotate(45deg) scaleX(1); }
          50% { transform: rotate(45deg) scaleX(1.5); }
        }

        @keyframes float-1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .animate-fade-in-up {
          opacity: 0;
          transform: translateY(40px);
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-wave {
          animation: wave 5s ease-in-out infinite;
        }

        .animate-float-1 {
          animation: float-1 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Projects;